import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, MessageSquare, Phone, Mail, Clock, CheckCircle, Search, Send } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import TaskSubmissionForm from "@/components/TaskSubmissionForm";

const priorityColors: Record<string, string> = {
  urgent: "bg-red-500 text-white",
  high: "bg-orange-500 text-white", 
  medium: "bg-yellow-500 text-black",
  low: "bg-green-500 text-white"
};

const statusColors: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 dark:bg-blue-950/20 dark:text-blue-200",
  in_progress: "bg-purple-100 text-purple-800 dark:bg-purple-950/20 dark:text-purple-200",
  pending_info: "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-200",
  resolved: "bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-200",
  closed: "bg-gray-100 text-gray-800 dark:bg-gray-950/20 dark:text-gray-200"
};

export default function Support() {
  const [ticketSearch, setTicketSearch] = useState("");
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [isTicketDialogOpen, setIsTicketDialogOpen] = useState(false);
  const [commentContent, setCommentContent] = useState("");
  
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Get current employee info
  const { data: currentEmployee } = useQuery<any>({
    queryKey: ["/api/employees/me"],
  });

  // console.log("[SUPPORT] Current employee data:", currentEmployee);

  // Tickets list query - filtered by current employee
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery<{ tasks: any[]; pagination?: any }>({
    queryKey: ["/api/tasks", { employeeId: currentEmployee?.id, q: ticketSearch || undefined, limit: 25 }],
    enabled: !!currentEmployee?.id,
    queryFn: async ({ queryKey }) => {
      const [_k, params] = queryKey as [string, any];
      const url = new URL("/api/tasks", window.location.origin);
      Object.entries(params).forEach(([k, v]) => { 
        if (v) url.searchParams.set(k, String(v)); 
      });
      console.log("[SUPPORT] Fetching tickets with URL:", url.toString());
      console.log("[SUPPORT] Query params:", params);
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load tasks");
      const data = await res.json();
      console.log("[SUPPORT] Tickets response:", data);
      console.log("[SUPPORT] Employee ID used for filtering:", currentEmployee?.id);
      return data;
    }
  });

  // Ticket detail query
  const { data: ticketDetail, refetch: refetchTicketDetail, isFetching: fetchingTicketDetail } = useQuery<any>({
    queryKey: selectedTicketId ? ["/api/tasks", selectedTicketId] : ["/api/tasks/idle"],
    enabled: !!selectedTicketId && isTicketDialogOpen,
    queryFn: async () => {
      const res = await fetch(`/api/tasks/${selectedTicketId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load task detail");
      return res.json();
    }
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!selectedTicketId) return;
      // Author identity is derived from the session server-side.
      const res = await apiRequest('POST', `/api/tasks/${selectedTicketId}/comments`, {
        content,
        authorType: 'employee'
      });
      return res.json();
    },
    onSuccess: async () => {
      await refetchTicketDetail();
      await queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      setCommentContent("");
      toast({ title: "Comment added", variant: "success" });
    },
    onError: () => {
      toast({ title: "Failed to add comment", variant: "destructive" });
    }
  });

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Support Center</h1>
          <p className="text-muted-foreground">
            Get help with benefits, payroll, and workplace questions
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Quick Help Cards */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Response Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{"< 4 hours"}</div>
            <p className="text-xs text-muted-foreground">
              Average response time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Support Hours</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">24/7</div>
            <p className="text-xs text-muted-foreground">
              Online support available
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolution Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">98%</div>
            <p className="text-xs text-muted-foreground">
              First contact resolution
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Support Options */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Submit a Task
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Get detailed help with benefits enrollment, payroll questions, and system issues. All support tasks are routed to our broker team for expert assistance and will be responded to within 4 hours.
            </p>
            <div className="space-y-2">
              <div className="text-sm">
                <strong>Best for:</strong>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Benefits enrollment questions</li>
                <li>• Payroll discrepancies</li>
                <li>• PTO request issues</li>
                <li>• System access problems</li>
                <li>• Document requests</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Call HR
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              For urgent matters that need immediate attention, call our HR hotline directly.
            </p>
            <div className="space-y-3">
              <div>
                <div className="font-medium">HR Hotline</div>
                <div className="text-lg font-bold text-blue-600">(555) 123-4567</div>
              </div>
              <div>
                <div className="text-sm font-medium">Hours:</div>
                <div className="text-sm text-muted-foreground">
                  Monday - Friday: 8:00 AM - 6:00 PM
                </div>
              </div>
              <div className="text-sm">
                <strong>Best for:</strong>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Emergency situations</li>
                <li>• Urgent benefits questions</li>
                <li>• Time-sensitive payroll issues</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Support
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Send detailed questions or requests via email for non-urgent matters.
            </p>
            <div className="space-y-3">
              <div>
                <div className="font-medium">HR Email</div>
                <div className="text-sm font-mono text-blue-600">hr@company.com</div>
              </div>
              <div>
                <div className="font-medium">Benefits Team</div>
                <div className="text-sm font-mono text-blue-600">benefits@company.com</div>
              </div>
              <div>
                <div className="text-sm font-medium">Response Time:</div>
                <div className="text-sm text-muted-foreground">1-2 business days</div>
              </div>
              <div className="text-sm">
                <strong>Best for:</strong>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• General inquiries</li>
                <li>• Policy clarifications</li>
                <li>• Documentation requests</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Ticket Submission Form */}
      <TaskSubmissionForm portalType="employee" />

      {/* Task List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            My Support Tasks
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            View and manage your submitted support requests
          </p>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search your tasks..."
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
                className="max-w-md"
              />
            </div>
          </div>

          {ticketsLoading ? (
            <div className="text-sm text-muted-foreground">Loading tasks...</div>
          ) : !ticketsData?.tasks?.length ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No support tasks found</p>
              <p className="text-sm text-muted-foreground">Submit a task above to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {ticketsData.tasks.map((ticket: any) => (
                <div 
                  key={ticket.id} 
                  className="p-4 border rounded-lg hover:bg-muted/50 transition cursor-pointer"
                  onClick={() => {
                    setSelectedTicketId(ticket.id);
                    setIsTicketDialogOpen(true);
                  }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium truncate">{ticket.title}</h3>
                        <Badge className={`text-xs ${priorityColors[ticket.priority] || 'bg-gray-500 text-white'}`}>
                          {ticket.priority}
                        </Badge>
                        <Badge className={`text-xs ${statusColors[ticket.status] || 'bg-gray-100 text-gray-800'}`}>
                          {ticket.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2 overflow-hidden" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical' as const
                      }}>
                        {ticket.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>#{ticket.ticketNumber}</span>
                        <span>{ticket.category}</span>
                        <span>Created {format(new Date(ticket.createdAt), 'MMM d, yyyy')}</span>
                        {ticket.lastActivityDate && (
                          <span>Last activity {format(new Date(ticket.lastActivityDate), 'MMM d')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task Detail Dialog */}
      <Dialog open={isTicketDialogOpen} onOpenChange={(open) => { 
        setIsTicketDialogOpen(open); 
        if(!open){ 
          setSelectedTicketId(null);
          setCommentContent("");
        } 
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {fetchingTicketDetail ? "Loading..." : ticketDetail ? `${ticketDetail.ticketNumber} • ${ticketDetail.title}` : "Task"}
            </DialogTitle>
          </DialogHeader>
          {ticketDetail && (
            <div className="space-y-6">
              {/* Task Status and Priority */}
              <div className="flex flex-wrap gap-2 items-center">
                <Badge className={`text-xs ${priorityColors[ticketDetail.priority] || 'bg-gray-500 text-white'}`}>
                  {ticketDetail.priority}
                </Badge>
                <Badge className={`text-xs ${statusColors[ticketDetail.status] || 'bg-gray-100 text-gray-800'}`}>
                  {ticketDetail.status.replace('_', ' ')}
                </Badge>
                <div className="text-xs text-muted-foreground ml-auto">
                  Created {format(new Date(ticketDetail.createdAt), 'MMM d, yyyy h:mm a')}
                </div>
              </div>

              {/* Description */}
              <div>
                <div className="text-sm font-medium mb-1">Description</div>
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                  {ticketDetail.description}
                </p>
              </div>

              {/* Comments */}
              <div className="space-y-2">
                <div className="text-sm font-medium">Comments & Updates</div>
                {ticketDetail.comments && ticketDetail.comments.length > 0 ? (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {ticketDetail.comments.map((comment: any) => (
                      <div key={comment.id} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-sm font-medium">{comment.authorName}</span>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(comment.createdAt), 'MMM d, h:mm a')}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No comments yet</p>
                )}
              </div>

              {/* Add Comment */}
              <div className="space-y-2">
                <div className="text-sm font-medium">Add Comment</div>
                <Textarea
                  placeholder="Add a comment or update..."
                  value={commentContent}
                  onChange={(e) => setCommentContent(e.target.value)}
                  rows={3}
                />
                <Button 
                  onClick={() => addCommentMutation.mutate(commentContent)}
                  disabled={!commentContent.trim() || addCommentMutation.isPending}
                  size="sm"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Add Comment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">How do I enroll in benefits?</h4>
              <p className="text-sm text-muted-foreground">
                Visit the Benefits page to view available plans and enroll during open enrollment periods or within 30 days of qualifying life events.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">When will I receive my pay stub?</h4>
              <p className="text-sm text-muted-foreground">
                Pay stubs are available in your employee portal by 5 PM on payday. Check the Payroll section for current and historical pay information.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">How do I request time off?</h4>
              <p className="text-sm text-muted-foreground">
                Submit PTO requests through the Benefits page at least two weeks in advance. Emergency requests can be submitted anytime with manager approval.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Where can I update my personal information?</h4>
              <p className="text-sm text-muted-foreground">
                Update your address, phone number, and emergency contacts in the Profile page. Some changes may require supporting documentation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}